import './analytics.ts';
import './styles.css';
import {
  DEFAULT_SITE_SETTINGS,
  api,
  fetchingFresh,
  type Forecast,
  type Overview,
  type RecordsResponse,
  type SeasonCard,
  type SpellsResponse,
  type SiteSettings,
  type Station,
  type Summary,
} from './api.ts';
import { applyWeatherBackground } from './background.ts';
import { applyCover } from './cover.ts';
import { el, escapeHtml, select, ymd } from './components/ui.ts';
import type { RangeKey } from './sections/days.ts';
import { adminMenu } from './sections/admin-menu.ts';
import { footerSection } from './sections/footer.ts';
import { currentDay, heroSection } from './sections/hero.ts';
import { extremesSection, recordTiles } from './sections/records.ts';
import { buildShareImage, openShareDialog } from './share.ts';
import { type ThemeChoice, onThemeChange, setThemeChoice, theme, themeChoice } from './theme.ts';

/**
 * De hero komt uit de hoofdbundle; alles met Chart.js erin wordt pas daarna
 * geladen. Zie ./charts.ts.
 */
type Charts = typeof import('./charts.ts');
let charts: Charts;

/** Beloftes die index.html al heeft afgevuurd voor de bundle er was. */
declare global {
  interface Window {
    __boot?: {
      summary: Promise<Summary>;
      overview: Promise<Overview>;
      station: Promise<Station>;
      live: Promise<Summary>;
    };
  }
}

const app = document.getElementById('app')!;

let overview: Overview;
let summary: Summary;
let records: RecordsResponse;
let spellData: SpellsResponse;
let station: Station;
let forecast: Forecast;

/**
 * De bovenste kaart en de voettekst staan buiten `swap()`: de hero heeft geen
 * id en de voettekst hangt aan `document.body` in plaats van aan `#app`. Ze
 * houden dus hun eigen handvat, zodat een verversing ze kan vervangen.
 */
let hero: HTMLElement;
let foot: HTMLElement | null = null;

let range: RangeKey = '30D';
let years: number[] = [];
/**
 * Jaar → kleurslot. Een jaar claimt bij selectie de laagste vrije kleur en houdt
 * die tot je het uitzet, zodat de overige lijnen niet herkleuren als je één jaar
 * aan- of uitzet. Er zijn zeven gevalideerde kleuren; meer jaren tegelijk kan niet.
 */
const yearSlots = new Map<number, number>();
const SLOT_COUNT = 7;

function claimSlot(year: number): boolean {
  if (yearSlots.has(year)) return true;
  const taken = new Set(yearSlots.values());
  for (let slot = 0; slot < SLOT_COUNT; slot++) {
    if (!taken.has(slot)) {
      yearSlots.set(year, slot);
      return true;
    }
  }
  return false;
}

let smoothing = 7;
/**
 * Welk soort extreme periode de grote kaart toont. De server kiest de
 * begintoestand (`suggested`); `heroSeason` staat aan zolang er niets recents
 * genoeg is en het lopende seizoen dus meer te vertellen heeft.
 */
let spellKind = 'heat';
let heroSeason = false;

/** Het soort periode dat het dichtst bij een seizoen ligt, voor het tabblad. */
function seasonKind(key: SeasonCard['key'] | undefined): string {
  return { winter: 'cold', spring: 'dry', summer: 'heat', autumn: 'wet' }[key ?? 'summer'];
}
/**
 * Het jaar waar de pagina op focust: stuurt de recordtegels, de tellingen, de
 * dikke lijn in de jaargrafiek én de uitgelichte neerslagcurve aan.
 */
let focusYear = new Date().getFullYear();
/**
 * De site-instellingen zoals de server ze kent. `weatherBg` is de stand die de
 * pagina nu toont; die loopt gelijk met de instelling, maar wordt apart
 * bijgehouden omdat de achtergrond al geverfd wordt voordat het beheerpaneel
 * bestaat.
 */
let siteSettings: SiteSettings = DEFAULT_SITE_SETTINGS;
let weatherBg = siteSettings.weatherBackground;

/** Zet de site-instellingen door naar wat er op het scherm staat. */
function applySettings(next: SiteSettings): void {
  siteSettings = next;
  weatherBg = next.weatherBackground;
  paintHero();
}

/**
 * De achtergrond van de hero, in één greep.
 *
 * De twee horen bij elkaar: de coverfoto vervaagt naar onder toe in de
 * weerachtergrond, dus wie de een verft zonder de ander laat een naad staan.
 * Alles wat de hero vervangt of het thema wisselt komt hier langs.
 */
function paintHero(): void {
  applyWeatherBackground(summary, weatherBg);
  if (hero) applyCover(hero, siteSettings.cover, theme() === 'dark');
}

function destroy(node: Element | null): void {
  node?.dispatchEvent(new CustomEvent('sectiondestroy'));
  node?.querySelectorAll('.panel').forEach((n) => n.dispatchEvent(new CustomEvent('sectiondestroy')));
}

/** Vervangt of plaatst één sectie, met opruimen van de oude grafieken. */
async function swap(id: string, build: () => Promise<HTMLElement> | HTMLElement): Promise<void> {
  const previous = document.getElementById(id);
  const next = await build();
  next.id = id;
  if (previous) {
    destroy(previous);
    previous.replaceWith(next);
  } else {
    app.append(next);
  }
}

/**
 * De hero opnieuw, uit de samenvatting die er nu ligt.
 *
 * De kaart is een zuivere functie van die samenvatting, dus hertekenen is
 * eenvoudiger en betrouwbaarder dan zeven losse waarden bijwerken. Wel zonder
 * `rise`: de kaart is al binnengevlogen, en dat een tweede keer doen leest als
 * een storing in plaats van als een verversing.
 */
function renderHero(): void {
  const next = heroSection(summary, station, () => void share());
  next.classList.remove('rise');
  hero.replaceWith(next);
  hero = next;
  // De coverfoto hangt aan het hero-element zelf, dus die moet mee met de
  // vervanging — anders staat er na een verversing een kale hero.
  paintHero();
}

/** De voettekst staat buiten `#app` en heeft dus zijn eigen vervanging nodig. */
function renderFooter(): void {
  const next = footerSection(station, forecast, summary, overview.coverage);
  if (foot) foot.replaceWith(next);
  else document.body.append(next);
  foot = next;
}

const renderDays = () =>
  swap('sec-days', () =>
    charts.daysSection(
      range,
      (key) => {
        range = key;
        void renderDays();
      },
      overview.has.rain,
    ),
  );

const renderYears = () =>
  swap('sec-years', () =>
    charts.yearsSection(overview, records, {
      years,
      smoothing,
      focus: focusYear,
      slots: yearSlots,
      onYears: (next) => {
        // Vrijgeven bij uitzetten, claimen bij aanzetten; de overige jaren
        // houden zo hun kleur.
        for (const year of years) if (!next.includes(year)) yearSlots.delete(year);
        years = next.filter((year) => claimSlot(year));
        void renderYears();
      },
    }),
  );

const renderRain = () => swap('sec-rain', () => charts.rainSection(overview, focusYear));

/** Alles wat van het uitgelichte jaar afhangt opnieuw tekenen. */
async function renderFocusYear(): Promise<void> {
  // Het uitgelichte jaar hoort ook in de vergelijkingsgrafiek te staan; anders
  // licht je een jaar uit dat daar niet te zien is. Zijn alle kleuren bezet, dan
  // maakt het oudste geselecteerde jaar plaats.
  if (!years.includes(focusYear)) {
    if (!claimSlot(focusYear)) {
      const oldest = [...years].sort((a, b) => a - b)[0];
      if (oldest !== undefined) {
        years = years.filter((y) => y !== oldest);
        yearSlots.delete(oldest);
        claimSlot(focusYear);
      }
    }
    if (yearSlots.has(focusYear)) years = [...years, focusYear];
  }

  records = await api.records(focusYear);
  await Promise.all([
    renderYears(),
    overview.has.rain ? renderRain() : Promise.resolve(),
    swap('sec-records', () => recordTiles(records)),
  ]);
}

const renderExtremes = () =>
  swap('sec-extremes', () =>
    extremesSection(spellData, {
      kind: spellKind,
      season: heroSeason,
      onKind: (kind) => {
        spellKind = kind;
        // Een tabklik is een vraag naar dát soort; de seizoenskaart maakt plaats.
        heroSeason = false;
        void renderExtremes();
      },
      onSeason: () => {
        heroSeason = true;
        void renderExtremes();
      },
    }),
  );

/* ---------------------------------------------------------------------- *
 *  Verversen zodra de bezoeker terugkeert                                *
 * ---------------------------------------------------------------------- */

/** Onder deze drempel is wat er staat nog vers genoeg voor een verzoek. */
const MIN_REFRESH_MS = 60_000;
/** Hierboven is ook het archief opgeschoven en moet de hele pagina opnieuw. */
const DEEP_REFRESH_MS = 3_600_000;

let lastRefresh = Date.now();
let refreshing = false;

/**
 * De archiefsecties opnieuw ophalen en tekenen.
 *
 * Alles binnen `fetchingFresh`, want deze routes staan op een half uur
 * browsercache: zonder die omzeiling zou de browser teruggeven wat er al staat.
 */
async function deepRefresh(): Promise<void> {
  // Nog aan het laden: main() zet de secties zo meteen zelf voor het eerst neer.
  if (!charts) return;

  await fetchingFresh(async () => {
    [records, spellData, forecast] = await Promise.all([
      api.records(focusYear),
      api.spells(),
      api.forecast(),
    ]);
    await renderDays();
    await renderYears();
    if (overview.has.rain) await renderRain();
    await swap('sec-records', () => recordTiles(records));
    // Bewust zonder `spellKind` en `heroSeason` opnieuw uit `suggested` af te
    // leiden: die twee zijn intussen misschien een klik van de bezoeker.
    await renderExtremes();
  });
  renderFooter();
}

/**
 * De bezoeker is terug: haal op wat er intussen veranderd is.
 *
 * Bewust niet in twee snelheden gedacht maar in twee dieptes. De hero gaat over
 * "nu buiten" en verandert elke vijf minuten, dus die gaat altijd mee — één
 * verzoek naar /live, dat op `no-store` staat en de server dus echt bereikt.
 * Het archief schuift maar één keer per uur op, met de ingest mee, dus dat komt
 * er alleen bij als je lang genoeg weg was om er iets van te merken.
 *
 * Een dagwissel telt daarbij als "lang weg", hoe kort de afwezigheid ook was:
 * na middernacht slaat "vandaag" om, verschuift het venster van de dagengrafiek
 * en gaan de recordtegels over een andere dag.
 *
 * `overview` en `focusYear` blijven met opzet staan. Alleen op nieuwjaarsnacht
 * zou daar een jaar bij moeten komen, en dat is het herbouwen van de
 * bedieningsbalk — en het weggooien van een expliciete jaarkeuze — niet waard.
 */
async function refresh(): Promise<void> {
  if (refreshing) return;

  const away = Date.now() - lastRefresh;
  const rolled = summary.today.date !== null && summary.today.date !== ymd(new Date());
  if (away < MIN_REFRESH_MS && !rolled) return;

  refreshing = true;
  try {
    const fresh = await api.live();
    if (fresh?.now) {
      summary = fresh;
      renderHero();
    }
    if (away >= DEEP_REFRESH_MS || rolled) await deepRefresh();
    lastRefresh = Date.now();
  } catch {
    // Mislukt de ophaling, dan blijft staan wat er stond — de gegevens uit de
    // database zijn hooguit oud, niet fout.
  } finally {
    refreshing = false;
  }
}

/**
 * Drie gebeurtenissen voor één vraag — "is de bezoeker terug?" — omdat geen
 * ervan die vraag alleen beantwoordt. `visibilitychange` is de gewone weg van
 * tab naar tab. `focus` vangt het wisselen tussen vensters op de desktop, waar
 * de tab zichtbaar blijft en `visibilityState` dus niet omslaat. `pageshow` met
 * `persisted` vangt de terugknop op iOS: die haalt de pagina uit de bfcache
 * zonder dat een van de andere twee afgaat.
 *
 * Ze vuren geregeld dubbel; de drempel in `refresh()` maakt dat onschadelijk.
 */
function watchReturn(): void {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void refresh();
  });
  window.addEventListener('focus', () => void refresh());
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) void refresh();
  });
}

/**
 * De deelbare afbeelding maakt de dag die de hero-curve op dat moment toont.
 *
 * Die reeks is er al — vandaag komt uit /summary, een oudere dag uit de
 * ophaling die het bladeren zelf deed — dus delen kost geen extra verzoek.
 */
async function share(): Promise<void> {
  try {
    const day = currentDay();
    const blob = await buildShareImage(summary, station, day);
    openShareDialog(blob, summary, station, day);
  } catch (error) {
    console.error('[share]', error);
  }
}

/**
 * De drie standen van de themaknop, in de volgorde waarin hij ze doorloopt.
 *
 * "Systeem" staat vooraan en is de beginstand: zolang je niets kiest volgt de
 * pagina het toestel, inclusief een nachtstand die vanzelf omslaat. Pas wie
 * expliciet klikt, krijgt een keuze die blijft staan.
 */
const THEMES: { choice: ThemeChoice; icon: string; label: string }[] = [
  { choice: 'system', icon: '◐', label: 'Systeem' },
  { choice: 'light', icon: '☀', label: 'Licht' },
  { choice: 'dark', icon: '☾', label: 'Donker' },
];

/**
 * De themakeuze staat in Navy onderaan bij de andere instellingen en niet meer
 * naast de deelknop in de hero.
 *
 * Het ontwerp tekent er een aan/uit-schakelaar, maar dat kan hier niet: er zijn
 * drie standen en de derde — "systeem" — is de beginstand en de nuttigste, want
 * die volgt de nachtstand van het toestel. Een schakelaar met twee standen zou
 * die stilzwijgend weggooien zodra je hem één keer aanraakt. Dit is dus een
 * knop die doorloopt, in dezelfde maat als de rest van de balk, en hij toont
 * altijd welke stand actief is.
 */
function themeButton(): HTMLButtonElement {
  const button = el('button', 'btn-ghost');
  button.type = 'button';

  const show = () => {
    const current = THEMES.find((t) => t.choice === themeChoice()) ?? THEMES[0]!;
    const next = THEMES[(THEMES.indexOf(current) + 1) % THEMES.length]!;
    button.innerHTML = `<span aria-hidden="true">${current.icon}</span> ${escapeHtml(current.label)}`;
    button.setAttribute('aria-label', `Thema: ${current.label.toLowerCase()}`);
    button.title = `Thema staat op ${current.label.toLowerCase()} — klik voor ${next.label.toLowerCase()}`;
  };

  show();
  button.addEventListener('click', () => {
    const index = THEMES.findIndex((t) => t.choice === themeChoice());
    setThemeChoice(THEMES[(index + 1) % THEMES.length]!.choice);
    show();
    /*
     * Ook verven als het thema níet omslaat.
     *
     * `setThemeChoice` schrijft altijd de `theme-color`, maar waarschuwt de
     * luisteraars alleen als de opgeloste stand verandert — die guard voorkomt
     * dat de grafieken bij elke klik opnieuw getekend worden. Van "licht" naar
     * "systeem" springen op een licht toestel verandert niets aan het scherm,
     * maar zet die metatag wel terug op de kale paginakleur. Zonder deze regel
     * blijft de statusbalk op een telefoon dan als een bleke band boven de
     * coverfoto staan.
     */
    paintHero();
  });

  return button;
}

/** Onderste bedieningsbalk: instellingen die meerdere secties raken. */
function controls(): HTMLElement {
  const bar = el('div', 'controls');
  const set = el('div', 'set');

  set.append(
    select(
      'Afvlakking',
      [
        { value: '1', label: 'ruw' },
        { value: '7', label: '7 dagen' },
        { value: '31', label: '31 dagen' },
      ],
      String(smoothing),
      (v) => {
        smoothing = Number.parseInt(v, 10);
        void renderYears();
      },
    ),
    select(
      'Uitgelicht jaar',
      overview.years.map((y) => ({ value: String(y), label: String(y) })).reverse(),
      String(focusYear),
      (v) => {
        focusYear = Number.parseInt(v, 10);
        void renderFocusYear();
      },
    ),
  );

  const themeLabel = el('label');
  themeLabel.append(document.createTextNode('Thema'), themeButton());
  set.append(themeLabel);

  /*
   * De schakelaar voor de weerachtergrond zat hier los in de balk en stond voor
   * iedereen open. Hij is nu een site-instelling en zit in het beheermenu
   * rechtsbovenaan: hij geldt voor alle bezoekers, dus verandert alleen de
   * beheerder hem. Wat hier blijft staan zijn de weergaveknoppen — afvlakking,
   * uitgelicht jaar en thema — want die zijn van de bezoeker.
   */

  bar.append(set);
  bar.append(
    el(
      'div',
      'meta-line',
      `Netatmo-archief · ${summary.measuredDays.toLocaleString('nl-BE')} meetdagen · archief elk uur, meting van nu bij het openen en bij terugkeer`,
    ),
  );
  return bar;
}

function notice(html: string): void {
  app.replaceChildren(el('div', 'notice', html));
}

async function main(): Promise<void> {
  // De beloftes uit index.html zijn al onderweg; alleen als die er niet zijn
  // (bijvoorbeeld in de dev-server na een hot reload) vragen we zelf op.
  const boot = window.__boot ?? {
    summary: api.summary(),
    overview: api.overview(),
    station: api.station(),
    live: api.live(),
  };

  // Naast de beloftes uit index.html, want die kent deze route nog niet. Een
  // mislukte ophaling mag de pagina niet tegenhouden: dan staat de site er met
  // de standaardinstellingen, wat precies is wat er vóór dit alles gebeurde.
  const settings = api.settings().catch(() => DEFAULT_SITE_SETTINGS);
  /*
   * En een tráge ophaling evenmin. Een `catch` vangt alleen een afwijzing; een
   * verzoek dat blijft hangen wijst nooit af, en dan blijft het hele dashboard
   * zwart achter een instelling die een prima standaard heeft. Na anderhalve
   * seconde verft de pagina dus met die standaard, en past hij de echte waarde
   * alsnog toe zodra ze binnenkomt.
   */
  const settingsOfStandaard = Promise.race([
    settings,
    new Promise<SiteSettings>((resolve) => {
      setTimeout(() => resolve(DEFAULT_SITE_SETTINGS), 1500);
    }),
  ]);

  try {
    [overview, summary, station] = await Promise.all([boot.overview, boot.summary, boot.station]);
  } catch (error) {
    notice(
      `De server antwoordt niet: ${escapeHtml(error instanceof Error ? error.message : String(error))}`,
    );
    return;
  }

  if (overview.years.length === 0) {
    notice(
      `<strong>Nog geen data.</strong> Draai eerst <code>npm run seed</code> en daarna
       <code>npm run backfill</code> om je historiek op te halen.`,
    );
    return;
  }

  focusYear = summary.rain.year;
  years = overview.years.slice(-3).filter((year) => claimSlot(year));

  // Vóór de eerste verf: anders staat de achtergrond even op de standaard en
  // springt hij zichtbaar om zodra de instellingen binnen zijn.
  siteSettings = await settingsOfStandaard;
  weatherBg = siteSettings.weatherBackground;
  applyWeatherBackground(summary, weatherBg);
  // De coverfoto komt er zodra de hero bestaat; zie een paar regels verderop.
  void settings.then(applySettings);

  /*
   * Bij een themawissel past CSS zichzelf aan; canvas niet.
   *
   * De grafieken zijn met opgeloste kleurwaarden geverfd, dus die moeten
   * opnieuw. De tegels, tabellen en de hero-curve staan in CSS respectievelijk
   * SVG en volgen vanzelf — die blijven dus staan. Dat de drie grafieksecties
   * hun data opnieuw ophalen is bewust geaccepteerd: het is dezelfde weg die een
   * klik op een periodeknop aflegt, en de API staat naast de deur.
   */
  onThemeChange(() => {
    // Ook de sluier over de coverfoto hangt van het thema af: in het donkere
    // thema is een lichte foto het probleem, in het lichte een donkere.
    paintHero();
    if (!charts) return; // themawissel vóór de grafiekbibliotheek geladen is
    charts.applyChartDefaults();
    void renderDays();
    void renderYears();
    if (overview.has.rain) void renderRain();
  });

  // De bovenste kaart staat er nu — zonder dat Chart.js geladen hoefde te zijn.
  // Deze eerste keer mét `rise`: de kaart vliegt binnen.
  hero = heroSection(summary, station, () => void share());
  /*
   * Het beheermenu staat vóór de hero in de DOM, al hangt het er visueel
   * overheen: zo is het het eerste wat je met de tab-toets bereikt, in plaats
   * van na de hele pagina.
   */
  app.replaceChildren(
    // `read` in plaats van een waarde: de instellingen kunnen later binnenkomen
    // dan dit menu bestaat, en dan moet het de nieuwe zien in plaats van de
    // standaard waarmee de pagina begon.
    adminMenu({ read: () => siteSettings, write: applySettings }),
    hero,
  );
  paintHero();

  /*
   * En dan de verse meting, een tel later. Dezelfde weg die een verversing bij
   * terugkeer aflegt — zie renderHero().
   *
   * Mislukt de ophaling, dan blijft gewoon staan wat er stond — de gegevens uit
   * de database zijn hooguit een uur oud, niet fout.
   */
  void boot.live
    .then((fresh) => {
      if (!fresh?.now) return;
      summary = fresh;
      renderHero();
      // Vanaf hier telt de klok van de verversing: een alt-tab meteen na het
      // laden hoeft niet nog eens hetzelfde op te halen.
      lastRefresh = Date.now();
    })
    .catch(() => {});

  // Pas hierna de grafiekbibliotheek en de rest van de data.
  const [loaded, rest] = await Promise.all([
    import('./charts.ts'),
    Promise.all([api.records(focusYear), api.spells(), api.forecast()]),
  ]);
  charts = loaded;
  [records, spellData, forecast] = rest;
  // Geen enkele periode is nog nieuws: dan opent de kaart op het seizoen, met
  // het tabblad dat er het dichtst bij ligt eronder.
  heroSeason = spellData.suggested === null && spellData.season !== null;
  spellKind = spellData.suggested ?? seasonKind(spellData.season?.key);
  charts.applyChartDefaults();

  await renderDays();
  await renderYears();
  if (overview.has.rain) await renderRain();
  await swap('sec-records', () => recordTiles(records));
  await renderExtremes();

  app.append(controls());
  renderFooter();

  // Pas nu de pagina compleet is: eerder zou een terugkeer een verversing
  // starten naast een opbouw die nog bezig is.
  watchReturn();
}

void main();
